import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { getVendorLocations, IVendorLocation } from '@/core/services/vendors'
import { CreateLocationModal } from '@/home/components/vendors/CreateLocationModal'
import { EditLocationModal } from '@/home/components/vendors/EditLocationModal'
import Loader from '@/common/components/Loader'

interface LocationsTabProps {
  vendorId: string
  onError: (message: string) => void
  createModalOpen: boolean
  onCreateModalChange: (open: boolean) => void
}

export const LocationsTab = ({
  vendorId,
  onError,
  createModalOpen,
  onCreateModalChange,
}: LocationsTabProps) => {
  const navigate = useNavigate()
  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'VENDORS' })
  const [locations, setLocations] = useState<IVendorLocation[]>([])
  const [loading, setLoading] = useState(false)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [editLocation, setEditLocation] = useState<IVendorLocation | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const fetchLocations = useCallback(async () => {
    if (!vendorId) return
    setLoading(true)
    try {
      const { data } = await getVendorLocations(vendorId)
      setLocations(data)
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  const handleSelectAll = () => {
    if (!hasEditPermission) return
    setIsCheckAll(!isCheckAll)
    setIsCheck(!isCheckAll ? locations.map((l) => l.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) return
    setIsCheck(
      checked ? [...isCheck, id] : isCheck.filter((item) => item !== id),
    )
  }

  const handleEdit = (location: IVendorLocation) => {
    setEditLocation(location)
    setEditModalOpen(true)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row: IVendorLocation) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row: IVendorLocation) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            navigate(
              `/vendors/${festivalName}/${editionId}/${vendorId}/location/${row.id}`,
            )
          }}
        >
          {row.name}
        </a>
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row: IVendorLocation) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleEdit(row)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <Table
        className="[&_th]:!whitespace-nowrap"
        columns={columns}
        data={locations}
        isCheck={isCheck}
        rowReorder
        onRowReorder={() => {}}
      />

      <EditLocationModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        vendorId={vendorId}
        location={editLocation}
        onSuccess={fetchLocations}
      />

      <CreateLocationModal
        open={createModalOpen}
        onOpenChange={onCreateModalChange}
        vendorId={vendorId}
        onSuccess={fetchLocations}
        onError={onError}
      />
    </>
  )
}
